/**
 * Compact inline trend sparkline — one per customer row, showing margin %
 * across their recent orders for this product. Hand-rolled SVG: no
 * charting library exists in this repo (see margins/MarginSummaryChart.tsx,
 * the only other chart, also hand-rolled). Module-local rather than
 * promoted to @/components/ui since nothing else uses it yet (per
 * ops/CLAUDE.md's "≥2 consumers" rule for shared primitives).
 *
 * Per dataviz guidance: a stat-tile-style "12-point sparkline" — the line
 * itself stays a single de-emphasis (muted) color; only the current/latest
 * point is highlighted in the accent color. Direction (up/down) is
 * conveyed separately by a delta chip, never by recoloring the line.
 */
export interface SparklinePoint {
  date: string
  value: number
}

export interface SparklineProps {
  points: SparklinePoint[]
  width?: number
  height?: number
  ariaLabel: string
}

export function Sparkline({ points, width = 64, height = 20, ariaLabel }: SparklineProps) {
  if (points.length === 0) {
    return <span className="text-role-metadata text-tertiary">—</span>
  }
  if (points.length === 1) {
    return (
      <svg width={width} height={height} role="img" aria-label={ariaLabel}>
        <circle cx={width / 2} cy={height / 2} r={3} fill="var(--color-accent)" />
      </svg>
    )
  }

  const values = points.map((p) => p.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const padding = 3

  const coords = points.map((p, i) => {
    const x = (i / (points.length - 1)) * (width - padding * 2) + padding
    const y = height - padding - ((p.value - min) / range) * (height - padding * 2)
    return [x, y] as const
  })

  const path = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const [lastX, lastY] = coords[coords.length - 1]

  return (
    <svg width={width} height={height} role="img" aria-label={ariaLabel}>
      <path d={path} fill="none" stroke="var(--color-text-tertiary)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r={3} fill="var(--color-accent)" />
    </svg>
  )
}
